import { Delete, Edit, ExpandMore, Visibility } from '@mui/icons-material';
import { Box, Breadcrumbs, Button, Grid, Menu, MenuItem, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import CommonDialog from '../../../common/CommonDialog';
import LinkedDocumentLink, { firstLinked, getEstimateDetailUrl, getInvoiceDetailUrl } from '../../../common/LinkedDocumentLink';
import { useDeleteWorkOrder } from '../../../services/business/workOrders';
import { getItem } from '../../../utility/localStorage';
import WorkOrderDocument from '../WorkOrderCommonComponents/WorkOrderDocument';

const HEADER_BUTTON_STYLE = {
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.04px',
    fontSize: '14px',
    height: '42px',
    // Keep each label on one line — a narrow screen wraps the row, not the words inside a button.
    whiteSpace: 'nowrap',
};

/**
 * Draft work order detail.
 *
 * A draft has consumed no inventory and cannot be authorized or converted — the only
 * actions are edit and delete (spec §9 W4). Header layout matches the invoice draft page.
 */
const WorkOrderDraft = ({ workOrderId, workOrderData }) => {
    const navigate = useNavigate();
    const shopName = getItem('apex-saas_details')?.shopName;

    const [isDeleteModal, setIsDeleteModal] = useState(false);
    // MORE ACTIONS — View Customer / Delete, same as the estimate draft page.
    const [anchorEl, setAnchorEl] = useState(null);
    const handleCloseMenu = () => setAnchorEl(null);

    // Linked documents, shown the same way as on the other work order detail pages.
    const linkedInvoice = firstLinked(workOrderData?.linkedInvoiceId);
    const sourceEstimate = firstLinked(workOrderData?.linkedEstimateId);
    const deleteWorkOrder = useDeleteWorkOrder();

    const handleDelete = async () => {
        try {
            await deleteWorkOrder.mutateAsync(workOrderId);
            toast.success('Work order draft was successfully deleted');
            navigate('/work-orders/drafts');
        } catch (error) {
            console.error('Error deleting work order draft:', error);
        } finally {
            setIsDeleteModal(false);
        }
    };

    return (
        <Box padding={'32px 24px'} sx={{ bgcolor: 'background.main' }}>
            {/* position: relative — the links below are pinned to this header's top-right corner, so one
                link and two take exactly the same room and nothing shifts down. */}
            <Grid container sx={{ padding: '16px 0px', position: 'relative' }} gap={'8px'}>
                <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between',
                        gap: '8px',
                    }}
                >
                    <div role="presentation">
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link to="/work-orders/all" style={{ color: 'black', opacity: '0.6', textDecoration: 'none' }}>
                                Work Orders
                            </Link>
                            <Link to="/work-orders/drafts" style={{ color: 'black', opacity: '0.6', textDecoration: 'none' }}>
                                Drafts
                            </Link>
                            <Link to={`/work-orders/draft/${workOrderId}`} style={{ color: 'black', textDecoration: 'none' }}>
                                {workOrderData?.workOrderNumber}
                            </Link>
                        </Breadcrumbs>
                    </div>

                    {/* Linked documents: the invoice first, the source estimate beneath it. */}
                    <Stack
                        alignItems="flex-end"
                        spacing={0.5}
                        // Pinned to the header's corner on a normal screen; in the flow on a phone, where
                        // there is no room beside the breadcrumb.
                        sx={{ position: { xs: 'static', md: 'absolute' }, top: { md: '16px' }, right: 0 }}
                    >
                        {linkedInvoice?._id && <LinkedDocumentLink to={getInvoiceDetailUrl(linkedInvoice)} documentNumber={linkedInvoice?.invoiceNumber} />}
                        {sourceEstimate?._id && (
                            <LinkedDocumentLink to={getEstimateDetailUrl(sourceEstimate)} documentNumber={sourceEstimate?.estimateNumber} />
                        )}
                    </Stack>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Typography variant="h4" sx={{ fontWeight: '500' }}>
                        {workOrderData?.workOrderNumber}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="end">
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    {/* Wraps onto more rows when narrow — left aligned there, right aligned once there is room. */}
                    <Stack
                        direction={'row'}
                        spacing={2}
                        useFlexGap
                        sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, rowGap: '12px' }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            className="btn-text-size-manage"
                            startIcon={<Edit />}
                            onClick={() => navigate(`/work-orders/edit/${workOrderId}`)}
                            style={HEADER_BUTTON_STYLE}
                        >
                            Edit Work Order
                        </Button>

                        <Button
                            variant="contained"
                            color="greyColor"
                            id="work-order-draft-actions-button"
                            aria-controls={anchorEl ? 'work-order-draft-actions-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={anchorEl ? 'true' : undefined}
                            onClick={(event) => setAnchorEl(event.currentTarget)}
                            size="large"
                            style={{
                                ...HEADER_BUTTON_STYLE,
                                width: '182px',
                                letterSpacing: '0.4px',
                                color: '#000000',
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.25)',
                            }}
                        >
                            more actions
                            <ExpandMore style={{ marginLeft: '8px' }} />
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <WorkOrderDocument workOrderData={workOrderData} shopName={shopName} />

            <Menu
                style={{ top: 3 }}
                id="work-order-draft-actions-menu"
                aria-labelledby="work-order-draft-actions-button"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                // Same width as the MORE ACTIONS button and sitting directly under it, like the estimate and
                // invoice menus (they hard-code the button's width; here it is measured, so it always matches).
                sx={{
                    '& .MuiPaper-root': { width: '182px', minWidth: 'unset!important' },
                    '& .MuiMenuItem-root': { padding: '6px 12px!important', width: '100%' },
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleCloseMenu();
                        navigate(`/customers/${workOrderData?.customer?._id}/${workOrderData?.customer?.createdFrom}`);
                    }}
                >
                    <Visibility style={{ marginRight: '8px', fontSize: '20px' }} /> View Customer
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleCloseMenu();
                        setIsDeleteModal(true);
                    }}
                    style={{ color: '#D32F2F' }}
                >
                    <Delete style={{ marginRight: '8px', color: '#D32F2F', fontSize: '20px' }} /> Delete Work Order
                </MenuItem>
            </Menu>

            <CommonDialog
                open={isDeleteModal}
                onClose={() => setIsDeleteModal(false)}
                onCancel={() => setIsDeleteModal(false)}
                onSave={handleDelete}
                loader={deleteWorkOrder.isPending}
                dialogTitle="Delete draft?"
                dialogSubtitle="This work order draft will be permanently deleted. This action cannot be undone."
                cancelButtonText="CANCEL"
                saveButtonText="DELETE"
            />
        </Box>
    );
};

export default WorkOrderDraft;
